import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ERROR_CODES } from '@icebreaker/shared/errors';
import { STAFF_LIMITS, STAFF_ROLES, type StaffRole } from '@icebreaker/shared/staff';
import {
  Button,
  Field,
  Input,
  Modal,
  Select,
  fieldStyles,
  useErrorMessage,
  useFormErrors,
} from '@icebreaker/ui';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { inviteFormReset, inviteStaff } from '../store/staff-accounts.slice';
import styles from './admin.module.css';

/**
 * PERSONEL DAVET KATMANI (Karar 4c). Hesap hemen açılır ve kişiye 48 saatlik,
 * tek kullanımlık kurulum bağlantısı gider; şifreyi kişi kendisi belirler.
 *
 * ADRES HATALARI ALANIN ALTINA YAZILIR: `STAFF_EMAIL_TAKEN` ve
 * `STAFF_EMAIL_DOMAIN_NOT_ALLOWED` tek bir alanı reddeder (adresi), sunucu bu
 * kodları `fieldErrors` olmadan döner. "Girdiğin bilgilerde hata var" ile
 * geçiştirmek bu depoda ölçülmüş bir kusurdur; kod alan altında cümleye döner
 * ve yönetici NEDEN reddedildiğini görür (kapanış kriteri 7).
 */
export function InviteStaffDialog({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const errorMessage = useErrorMessage();
  const invite = useAppSelector((state: RootState) => state.staffAccounts.invite);
  const formErrors = useFormErrors(invite.error, INLINE_ERROR_PATHS);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<StaffRole>('moderator');

  // Katman her açılışta temiz başlar: önceki denemenin alanı ve hatası taşınmaz.
  useEffect(() => {
    if (!open) return;
    setEmail('');
    setDisplayName('');
    setRole('moderator');
    dispatch(inviteFormReset());
  }, [open, dispatch]);

  /*
   * KAPANIŞ HER YOLDA FORMU SIFIRLAR. Gerçek koşumda ölçüldü: ilk davet başarıyla
   * kapandıktan sonra `invite.phase` `succeeded` kalıyordu; katman ikinci kez
   * açıldığında açılış sıfırlaması ile "başarıda kapan" etkisi AYNI geçişte
   * koşuyor, ikincisi bayat `succeeded`ı okuyup katmanı anında kapatıyordu —
   * yönetici art arda iki kişiyi davet edemiyordu (sayfa yenilemeden).
   */
  const close = useCallback(() => {
    dispatch(inviteFormReset());
    onClose();
  }, [dispatch, onClose]);

  // Davet gitti → katman kapanır; bildirim cümlesi ekranda (`lastNotice`).
  useEffect(() => {
    if (open && invite.phase === 'succeeded') close();
  }, [open, invite.phase, close]);

  /*
   * ERKEN `return null` YOK: katman kapanınca bileşen AYAKTA kalır ve kitin odak
   * sözleşmesi (`useDialogFocus`: kapanışta odak açan düğmeye DÖNER) koşabilir.
   * İlk kanıt koşumunda Esc sonrası odak "Personel davet et"e dönmüyordu —
   * bileşen `if (!open) return null` ile birlikte kancayı da söküyordu.
   */
  const emailCodeSentence =
    invite.error && EMAIL_ERROR_CODES.has(invite.error.code) ? errorMessage(invite.error) : null;
  const emailError = formErrors.at('email') ?? emailCodeSentence ?? undefined;
  const summary = formErrors.summary.filter((sentence) => sentence !== emailCodeSentence);

  const canSubmit =
    email.trim().length > 0 && displayName.trim().length >= 2 && invite.phase !== 'loading';

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    dispatch(inviteStaff.request({ email: email.trim(), displayName: displayName.trim(), role }));
  };

  return (
    <Modal
      open={open}
      kicker={t.t('admin.staff.inviteDialog.kicker')}
      title={t.t('admin.staff.inviteDialog.title')}
      closeLabel={t.t('common.close')}
      onClose={close}
    >
      <p className={styles.detailBody}>
        {t.t('admin.staff.inviteDialog.body', { hours: STAFF_LIMITS.invitationTtlHours })}
      </p>
      <form onSubmit={submit} className={styles.inviteForm}>
        <Field
          id="staff-invite-email"
          label={t.t('admin.staff.inviteDialog.emailLabel')}
          hint={t.t('admin.staff.inviteDialog.emailHint')}
          error={emailError}
        >
          <Input
            id="staff-invite-email"
            name="email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            invalid={Boolean(emailError)}
            required
          />
        </Field>
        <Field
          id="staff-invite-name"
          label={t.t('admin.staff.inviteDialog.nameLabel')}
          error={formErrors.at('displayName')}
        >
          <Input
            id="staff-invite-name"
            name="displayName"
            autoComplete="off"
            minLength={2}
            maxLength={120}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            invalid={Boolean(formErrors.at('displayName'))}
            required
          />
        </Field>
        <Field
          id="staff-invite-role"
          label={t.t('admin.staff.inviteDialog.roleLabel')}
          hint={t.t(
            role === 'admin'
              ? 'admin.staff.inviteDialog.roleAdminHint'
              : 'admin.staff.inviteDialog.roleModeratorHint',
          )}
          error={formErrors.at('role')}
        >
          <Select
            id="staff-invite-role"
            name="role"
            value={role}
            invalid={Boolean(formErrors.at('role'))}
            onChange={(event) => setRole(event.target.value as StaffRole)}
          >
            {STAFF_ROLES.map((value) => (
              <option key={value} value={value}>
                {t.t(`admin.staff.roles.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        {/* Özet cümleler ÇEVRİLMİŞTİR; dikişe verilmez (error-message.guard KURAL 5b). */}
        {summary.map((sentence) => (
          <p key={sentence} className={fieldStyles.error} role="alert">
            {sentence}
          </p>
        ))}
        <div className={styles.inviteActions}>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            loading={invite.phase === 'loading'}
            loadingLabel={t.t('common.sending')}
          >
            {t.t('admin.staff.inviteDialog.submit')}
          </Button>
          <Button type="button" variant="ghost" onClick={close}>
            {t.t('common.cancel')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Cümlesi ALANIN ALTINA yazılan sunucu yolları. */
const INLINE_ERROR_PATHS = ['email', 'displayName', 'role'];

/** Alanı belli olan kodlar: ikisi de yalnız ADRESİ reddeder. */
const EMAIL_ERROR_CODES = new Set<string>([
  ERROR_CODES.STAFF_EMAIL_TAKEN,
  ERROR_CODES.STAFF_EMAIL_DOMAIN_NOT_ALLOWED,
]);
